import styled from '@emotion/styled';

import { BasicFlex } from '../../utils/Common';

import Field from '../../utils/FormikFields/Field';

import { Switch, TextField } from '@mui/material';

import { usePageData } from '../../contexts/PageData';

const SectionStyles = styled(BasicFlex)`
  margin: 1rem 0;
  /* margin-bottom: 2rem; */
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`

const SectionHeader = styled(BasicFlex)`
  align-items: flex-start;

`

const HebrewSpan = styled.span`
  font-family: "Bona Nova";
`

const SectionDescription = styled(BasicFlex)`

`
const SectionTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 500;
`

const SectionSubheader = styled.div`
  color: ${props => props.theme.colors.mildText};
`

const MainAction = styled.div``

const MainDisplay = styled.div`
  width: 100%;
`

const Panel = styled(BasicFlex)`
  gap: 1rem;
  flex-direction: column;
`

const TagRuleDescription = styled.div`

`

const TagRules = styled.ul`
  margin-top: 1rem;
  padding: 0;
  list-style: none;
  /* font-size: 15px; */
  line-height: 1.6;
  color: ${props => props.theme.colors.mildText};
`;

const TagField = styled(TextField)`
  margin: 1.5rem 0;
`

const ErrorList = styled.ul`
  margin: 1rem 0;
  padding: 0;
  list-style: none;
  line-height: 1.6;
  color: ${props => props.theme.colors.error};
`


const Section = ({ title, description, mainAction, children }) => {

  return (
    <SectionStyles>
      <SectionHeader>
        <SectionDescription dir="column" gap=".5rem">
          <SectionTitle>{title}</SectionTitle>
          <SectionSubheader>{description}</SectionSubheader>
        </SectionDescription>
        {mainAction ? <MainAction>{mainAction}</MainAction> : null}
      </SectionHeader>
      {children ? <MainDisplay>
        {children}
      </MainDisplay> : null}
    </SectionStyles>
  )
};

const SwitchSection = ({ title, description, mainAction, children, switchLabel, switchName }) => {

  const { data, modifySetting } = usePageData() || {};

  let values = data?.data || {};

  let id = data?.setting_id;

  const handleChange = (e) => {
    let val = e.target.checked;
    modifySetting(val, switchName, id);
  }
  return (
    <Section title={title} description={description} mainAction={<Switch label={switchLabel} name={switchName} checked={values[switchName]} onChange={handleChange} />} children={children} />
  )

};

const validateTemplate = (template, validPH = []) => {
  const errors = [];
  const warnings = [];
  
  // Valid placeholder names
  const validPlaceholders = validPH?.length ? validPH : [
    'event_title',
    'client_name',
    'stylist_name',
    'date',
    'start_time',
    'end_time'
  ];
  
  // 1. Check for mismatched brackets
  const openingBrackets = (template.match(/\{\{/g) || []).length;
  const closingBrackets = (template.match(/\}\}/g) || []).length;
  
  if (openingBrackets !== closingBrackets) {
    errors.push(
      `Mismatched brackets: ${openingBrackets} opening {{, ${closingBrackets} closing }}`
    );
  }
  
  // 2. Check for single brackets (common mistake)
  // First remove all valid {{ and }} pairs, then check for remaining single brackets
  const withoutValidBrackets = template.replace(/\{\{/g, '').replace(/\}\}/g, '');
  const singleOpenBrackets = withoutValidBrackets.match(/\{/g);
  const singleCloseBrackets = withoutValidBrackets.match(/\}/g);
  
  if (singleOpenBrackets) {
    errors.push(
      `Found ${singleOpenBrackets.length} single opening bracket(s) '{'. Did you mean '{{'?`
    );
  }
  
  if (singleCloseBrackets) {
    errors.push(
      `Found ${singleCloseBrackets.length} single closing bracket(s) '}'. Did you mean '}}'?`
    );
  }
  
  // 3. Extract all placeholders and validate them
  const placeholderPattern = /\{\{([^}]+)\}\}/g;
  const foundPlaceholders = [];
  let match;
  
  while ((match = placeholderPattern.exec(template)) !== null) {
    const placeholder = match[1].trim();
    foundPlaceholders.push({
      raw: match[0],
      name: placeholder,
      originalName: match[1],
      position: match.index
    });
  }
  
  // 4. Check each placeholder
  foundPlaceholders.forEach(({ raw, name, originalName, position }) => {
    // Check for extra spaces
    if (name !== originalName) {
      warnings.push(
        `Extra whitespace in placeholder "${raw}" at position ${position}`
      );
    }
    
    // Check if placeholder name is valid
    if (!validPlaceholders.includes(name)) {
      errors.push(
        `Invalid placeholder "{{${name}}}" at position ${position}. ` +
        `Valid options: ${validPlaceholders.join(', ')}`
      );
    }
    
    // Check for typos (using Levenshtein-like simple check)
    if (!validPlaceholders.includes(name)) {
      const suggestions = validPlaceholders.filter(valid => {
        // Simple similarity check - within 2 characters difference
        return Math.abs(valid.length - name.length) <= 2 && 
               (valid.includes(name.substring(0, 3)) || 
                name.includes(valid.substring(0, 3)));
      });
      
      if (suggestions.length > 0) {
        errors[errors.length - 1] += ` Did you mean: ${suggestions.join(' or ')}?`;
      }
    }
  });
  
  // 5. Check for incomplete placeholders
  const incompletePlaceholders = template.match(/\{\{[^}]*$/g);
  if (incompletePlaceholders) {
    errors.push('Incomplete placeholder found at end of template (missing closing }})');
  }
  
  // 6. Check for empty placeholders
  if (template.includes('{{}}')) {
    errors.push('Empty placeholder {{}} found');
  }
  
  // 7. Check for nested brackets (unlikely but possible mistake)
  // Look for {{ followed by another {{ before we see }}
  if (template.match(/\{\{[^}]*\{\{/)) {
    errors.push('Nested opening brackets found. Each placeholder should be separate.');
  }
  
  // 8. Warn if critical placeholders are missing
  const criticalPlaceholders = ['event_title', 'client_name', 'stylist_name'];
  const missingCritical = criticalPlaceholders.filter(
    critical => !foundPlaceholders.some(p => p.name === critical)
  );
  
  if (missingCritical.length > 0) {
    warnings.push(
      `Missing recommended placeholders: ${missingCritical.join(', ')}`
    );
  }
  
  // 9. Check for duplicate placeholders (might be intentional, so just warn)
  const placeholderCounts = {};
  foundPlaceholders.forEach(({ name }) => {
    placeholderCounts[name] = (placeholderCounts[name] || 0) + 1;
  });
  
  Object.entries(placeholderCounts).forEach(([name, count]) => {
    if (count > 1) {
      warnings.push(`Placeholder "{{${name}}}" appears ${count} times`);
    }
  });
  
  // 10. Check template is not empty
  if (template.trim().length === 0) {
    errors.push('Template cannot be empty');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    foundPlaceholders: foundPlaceholders.map(p => p.name)
  };
};

export { Panel, Section, SwitchSection, HebrewSpan, TagRuleDescription, TagRules, TagField, ErrorList, validateTemplate };